import { baseApi } from '../Api';
import {
  META_GET_ALL,
  META_UPDATE,
  GET_ALL_BREAK_DEFINITIONS,
  SEARCH_BREAK_DEFINITIONS,
  GET_BREAK_DEFINITION_BY_ID,
  CREATE_BREAK_DEFINITION,
  UPDATE_BREAK_DEFINITION,
  DELETE_BREAK_DEFINITION,
  BULK_DELETE_BREAK_DEFINITION,
} from './Endpoints';

export const breakDefinitionApi = baseApi.injectEndpoints({
  endpoints: (build) => ({
    //  POST: Get all break definitions (with filters, sorting, pagination) — query variant
    getAllBreakDefinitionsList: build.query({
      query: (body) => ({
        url: GET_ALL_BREAK_DEFINITIONS,
        method: 'POST',
        data: body,
      }),
    }),

    //  POST: Search break definitions — query variant
    searchBreakDefinitionsList: build.query({
      query: (body) => ({
        url: SEARCH_BREAK_DEFINITIONS,
        method: 'POST',
        data: body,
      }),
    }),

    //  POST: Get all break definitions (with filters, sorting, pagination)
    getAllBreakDefinitions: build.mutation({
      query: (body) => ({
        url: GET_ALL_BREAK_DEFINITIONS,
        method: 'POST',
        data: body, // axiosBaseQuery uses 'data' for request body
      }),
      // transformResponse: (response) => response?.data || response,
    }),

    //  POST: Search break definitions
    searchBreakDefinitions: build.mutation({
      query: (body) => ({
        url: SEARCH_BREAK_DEFINITIONS,
        method: 'POST',
        data: body,
      }),
    }),

    //  GET: Get single break definition by ID
    getBreakDefinitionById: build.query({
      query: (id) => ({
        url: `${GET_BREAK_DEFINITION_BY_ID}/${id}`,
        method: 'GET',
      }),
    }),

    //  POST: Create new break definition
    createBreakDefinition: build.mutation({
      query: (body) => ({
        url: CREATE_BREAK_DEFINITION,
        method: 'POST',
        data: body,
      }),
      transformResponse: (raw) => {
        const code = `${raw?.errCode ?? ''}`.trim();

        // 409 → duplicate → throw error
        if (code === '409') {
          const err = new Error(raw?.errDescription ?? 'Duplicate entry');
          err.name = 'DuplicateError';
          err.data = raw;
          throw err;
        }

        // 410 → dependency error → return raw so component can show table
        if (code === '410') return raw;

        // 000 → success
        if (code === '000') return raw;

        // Any other error code → throw
        if (code && code !== '000') {
          const err = new Error(raw?.errDescription ?? 'Something went wrong');
          err.data = raw;
          throw err;
        }

        return raw;
      },
    }),

    //  PUT: Update existing break definition
    updateBreakDefinition: build.mutation({
      query: ({ id, body }) => ({
        url: `${UPDATE_BREAK_DEFINITION}/${id}`,
        method: 'PUT',
        data: body,
      }),
      transformResponse: (raw) => {
        const code = `${raw?.errCode ?? ''}`.trim();

        // 410 → dependency error → return raw so component can show table
        if (code === '410') return raw;

        // 000 → success
        if (code === '000') return raw;

        // Any other error code → throw
        if (code && code !== '000') {
          const err = new Error(raw?.errDescription ?? 'Something went wrong');
          err.data = raw;
          throw err;
        }

        return raw;
      },
    }),

    //  DELETE: Delete a break definition
    deleteBreakDefinition: build.mutation({
      query: ({ id }) => ({
        url: `${DELETE_BREAK_DEFINITION}/${id}`,
        method: 'DELETE',
      }),
      transformResponse: (raw) => {
        const code = `${raw?.errCode ?? ''}`.trim();

        // 410 → dependency error → return raw so component can show table
        if (code === '410') return raw;

        // 000 → success
        if (code === '000') return raw;

        // Any other error code → throw
        if (code && code !== '000') {
          const err = new Error(raw?.errDescription ?? 'Something went wrong');
          err.data = raw;
          throw err;
        }

        return raw;
      },
    }),

    // PUT: Bulk Delete work schedules
    bulkDeleteBreakDefinition: build.mutation({
      query: (body) => ({
        url: BULK_DELETE_BREAK_DEFINITION,
        method: 'DELETE',
        data: body,
      }),
      transformResponse: (raw) => {
        const code = `${raw?.errCode ?? ''}`.trim();

        // 410 → dependency error → return raw so component can show table
        if (code === '410') return raw;

        // 000 → success
        if (code === '000') return raw;

        // Any other error code → throw
        if (code && code !== '000') {
          const err = new Error(raw?.errDescription ?? 'Something went wrong');
          err.data = raw;
          throw err;
        }

        return raw;
      },
    }),

    // ============================
    // 🔹 META DATA APIs
    // ============================

    // GET: Get all meta-data
    getMetaData: build.query({
      query: ({ companyId, tableCode }) => ({
        url: META_GET_ALL,
        method: 'GET',
        params: { companyId, tableCode },
      }),
      // transformResponse: (response) => response?.data || response,
    }),

    // PUT: Update meta-data
    updateMetaData: build.mutation({
      query: (body) => ({
        url: META_UPDATE,
        method: 'PUT',
        data: body,
      }),
      // transformResponse: (response) => response?.data || response,
    }),
  }),
});

export const {
  // Meta API hooks
  useGetMetaDataQuery,
  useLazyGetMetaDataQuery,
  useUpdateMetaDataMutation,

  useGetAllBreakDefinitionsListQuery,
  useLazyGetAllBreakDefinitionsListQuery,
  useSearchBreakDefinitionsListQuery,
  useLazySearchBreakDefinitionsListQuery,

  useGetAllBreakDefinitionsMutation,
  useSearchBreakDefinitionsMutation,
  useGetBreakDefinitionByIdQuery,
  useLazyGetBreakDefinitionByIdQuery,
  useCreateBreakDefinitionMutation,
  useUpdateBreakDefinitionMutation,
  useDeleteBreakDefinitionMutation,
  useBulkDeleteBreakDefinitionMutation,
  useGetUserPrivilegesQuery,
  useLazyGetUserPrivilegesQuery,
} = breakDefinitionApi;
